import { useState, useEffect } from 'react'
import { useAuth } from '../../hooks/useAuth'
import { api } from '../../services/api'
import { M365User, M365UserLicense, UpdateUserRequest, getLicenseDisplayName } from '../../types/m365Users'

interface M365UserDetailModalProps {
  user: M365User
  customerId: string
  customerTenantId: string
  onClose: () => void
  onUserUpdated: (user: M365User) => void
}

export function M365UserDetailModal({ user, customerId, customerTenantId, onClose, onUserUpdated }: M365UserDetailModalProps) {
  const { getCustomerGraphAccessToken } = useAuth()
  const [loading, setLoading] = useState(false)
  const [loadingLicenses, setLoadingLicenses] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [licenses, setLicenses] = useState<M365UserLicense[]>([])

  const [formData, setFormData] = useState<UpdateUserRequest>({
    displayName: user.displayName,
    givenName: user.givenName || '',
    surname: user.surname || '',
    jobTitle: user.jobTitle || '',
    department: user.department || '',
    officeLocation: user.officeLocation || '',
    mobilePhone: user.mobilePhone || '',
    usageLocation: user.usageLocation || '',
  })

  useEffect(() => {
    const fetchLicenses = async () => {
      try {
        // Use customer tenant token
        const token = await getCustomerGraphAccessToken(customerTenantId)
        const response = await api.getM365User(token, customerId, user.id)
        setLicenses(response.licenses || [])
      } catch (err) {
        console.error('Failed to load license details:', err)
      } finally {
        setLoadingLicenses(false)
      }
    }
    fetchLicenses()
  }, [customerId, customerTenantId, user.id, getCustomerGraphAccessToken])

  const handleChange = (field: keyof UpdateUserRequest, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError(null)

    try {
      // Use customer tenant token for user updates
      const token = await getCustomerGraphAccessToken(customerTenantId)

      // Only send changed fields
      const updates: UpdateUserRequest = {}
      if (formData.displayName !== user.displayName) updates.displayName = formData.displayName
      if (formData.givenName !== (user.givenName || '')) updates.givenName = formData.givenName
      if (formData.surname !== (user.surname || '')) updates.surname = formData.surname
      if (formData.jobTitle !== (user.jobTitle || '')) updates.jobTitle = formData.jobTitle
      if (formData.department !== (user.department || '')) updates.department = formData.department
      if (formData.officeLocation !== (user.officeLocation || '')) updates.officeLocation = formData.officeLocation
      if (formData.mobilePhone !== (user.mobilePhone || '')) updates.mobilePhone = formData.mobilePhone
      if (formData.usageLocation !== (user.usageLocation || '')) updates.usageLocation = formData.usageLocation

      if (Object.keys(updates).length === 0) {
        onClose()
        return
      }

      const response = await api.updateM365User(token, customerId, user.id, updates)
      onUserUpdated(response.user)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update user')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={onClose}></div>

        <div className="inline-block w-full max-w-2xl my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-lg sm:align-middle">
          <form onSubmit={handleSubmit}>
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-medium text-gray-900">Edit User</h3>
              <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-500">
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="px-6 py-4 max-h-[70vh] overflow-y-auto">
              {error && (
                <div className="mb-4 bg-red-50 border border-red-200 rounded-md p-3 text-sm text-red-700">
                  {error}
                </div>
              )}

              {/* User info header */}
              <div className="flex items-center mb-6 pb-4 border-b border-gray-200">
                <div className={`h-12 w-12 rounded-full flex items-center justify-center text-white font-medium ${user.accountEnabled ? 'bg-primary-500' : 'bg-gray-400'}`}>
                  {user.displayName?.charAt(0)?.toUpperCase() || 'U'}
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-900">{user.userPrincipalName}</p>
                  <p className="text-sm text-gray-500">
                    {user.accountEnabled ? (
                      <span className="text-green-600">Account Enabled</span>
                    ) : (
                      <span className="text-red-600">Account Disabled</span>
                    )}
                  </p>
                </div>
              </div>

              {/* Form fields */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">First Name</label>
                  <input
                    type="text"
                    value={formData.givenName}
                    onChange={(e) => handleChange('givenName', e.target.value)}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Last Name</label>
                  <input
                    type="text"
                    value={formData.surname}
                    onChange={(e) => handleChange('surname', e.target.value)}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700">Display Name</label>
                  <input
                    type="text"
                    value={formData.displayName}
                    onChange={(e) => handleChange('displayName', e.target.value)}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Job Title</label>
                  <input
                    type="text"
                    value={formData.jobTitle}
                    onChange={(e) => handleChange('jobTitle', e.target.value)}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Department</label>
                  <input
                    type="text"
                    value={formData.department}
                    onChange={(e) => handleChange('department', e.target.value)}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Office Location</label>
                  <input
                    type="text"
                    value={formData.officeLocation}
                    onChange={(e) => handleChange('officeLocation', e.target.value)}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Mobile Phone</label>
                  <input
                    type="tel"
                    value={formData.mobilePhone}
                    onChange={(e) => handleChange('mobilePhone', e.target.value)}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700">Usage Location (ISO Country Code)</label>
                  <input
                    type="text"
                    value={formData.usageLocation}
                    onChange={(e) => handleChange('usageLocation', e.target.value.toUpperCase())}
                    maxLength={2}
                    placeholder="US"
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                  />
                  <p className="mt-1 text-xs text-gray-500">Required before assigning licenses (e.g., US, GB, CA)</p>
                </div>
              </div>

              {/* Licenses section */}
              <div className="mt-6 pt-4 border-t border-gray-200">
                <h4 className="text-sm font-medium text-gray-900 mb-3">Assigned Licenses</h4>
                {loadingLicenses ? (
                  <div className="flex items-center text-sm text-gray-500">
                    <svg className="animate-spin h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                    </svg>
                    Loading licenses...
                  </div>
                ) : licenses.length === 0 ? (
                  <p className="text-sm text-gray-500">No licenses assigned</p>
                ) : (
                  <div className="space-y-2">
                    {licenses.map((license) => (
                      <div key={license.skuId} className="bg-gray-50 rounded-md p-3">
                        <p className="text-sm font-medium text-gray-900">
                          {getLicenseDisplayName(license.skuPartNumber)}
                        </p>
                        <p className="text-xs text-gray-500">{license.skuPartNumber}</p>
                        {license.servicePlans.length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-1">
                            {license.servicePlans.slice(0, 5).map((plan) => (
                              <span
                                key={plan.servicePlanId}
                                className={`inline-flex items-center px-2 py-0.5 rounded text-xs ${
                                  plan.provisioningStatus === 'Success'
                                    ? 'bg-green-100 text-green-700'
                                    : plan.provisioningStatus === 'Disabled'
                                    ? 'bg-gray-100 text-gray-500'
                                    : 'bg-yellow-100 text-yellow-700'
                                }`}
                              >
                                {plan.servicePlanName}
                              </span>
                            ))}
                            {license.servicePlans.length > 5 && (
                              <span className="text-xs text-gray-500">
                                +{license.servicePlans.length - 5} more
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
              >
                {loading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}
